import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Search, Filter, TrendingUp, FileText,
  Scale, GraduationCap, Bookmark, BookmarkCheck, Eye, Calendar, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const KnowledgeHubPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [selectedCategory, selectedTag, searchQuery]);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Fetch categories
      const catRes = await fetch(`${apiUrl}/api/knowledge/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
      }

      // Fetch popular tags
      const tagsRes = await fetch(`${apiUrl}/api/knowledge/tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tagsRes.ok) {
        const tagsData = await tagsRes.json();
        setPopularTags(tagsData);
      }

      // Fetch bookmarks
      const bookmarksRes = await fetch(`${apiUrl}/api/knowledge/bookmarks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (bookmarksRes.ok) {
        const bookmarksData = await bookmarksRes.json();
        setBookmarks(bookmarksData.map(b => b.articleId));
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedTag) params.append('tag', selectedTag);
      if (searchQuery) params.append('search', searchQuery);

      const res = await fetch(`${apiUrl}/api/knowledge/articles?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      setError('Failed to load articles');
    }
  };

  const handleBookmark = async (articleId) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const isBookmarked = bookmarks.includes(articleId);

      if (isBookmarked) {
        // Remove bookmark
        const res = await fetch(`${apiUrl}/api/knowledge/bookmarks/${articleId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setBookmarks(bookmarks.filter(id => id !== articleId));
        }
      } else {
        // Add bookmark
        const res = await fetch(`${apiUrl}/api/knowledge/articles/${articleId}/bookmark`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setBookmarks([...bookmarks, articleId]);
        }
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleSeedData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/knowledge/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchArticles();
        await fetchInitialData();
      }
    } catch (error) {
      console.error('Error seeding data:', error);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'law_update': return <Scale className="w-5 h-5" />;
      case 'template': return <FileText className="w-5 h-5" />;
      case 'case_study': return <TrendingUp className="w-5 h-5" />;
      case 'training': return <GraduationCap className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'law_update': return 'bg-blue-100 text-blue-800';
      case 'template': return 'bg-green-100 text-green-800';
      case 'case_study': return 'bg-purple-100 text-purple-800';
      case 'training': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-teal-600 hover:text-teal-700 mb-4 flex items-center"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BookOpen className="w-8 h-8 mr-3 text-teal-600" />
                Knowledge Hub
              </h1>
              <p className="text-gray-600 mt-1">
                Your comprehensive resource for CSR excellence
              </p>
            </div>
            <Button
              onClick={handleSeedData}
              variant="outline"
              className="text-teal-600 border-teal-600"
            >
              Seed Sample Data
            </Button>
          </div>
        </div>

        {error && <AlertBanner type="error" message={error} className="mb-6" />}

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles, templates, case studies..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
            <Button
              onClick={() => {
                setSelectedCategory('');
                setSelectedTag('');
                setSearchQuery('');
              }}
              variant="outline"
              className="text-gray-600"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories & Tags */}
          <div className="lg:col-span-1 space-y-6">
            {/* Categories */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Filter className="w-5 h-5 mr-2 text-teal-600" />
                Categories
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    !selectedCategory
                      ? 'bg-teal-50 text-teal-900 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All Articles
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      selectedCategory === cat.id
                        ? 'bg-teal-50 text-teal-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center">
                      {getCategoryIcon(cat.id)}
                      <span className="ml-2">{cat.name}</span>
                    </span>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">
                      {cat.articleCount}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Tags */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Tag className="w-5 h-5 mr-2 text-teal-600" />
                Popular Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularTags.map(tagObj => (
                  <button
                    key={tagObj.tag}
                    onClick={() => setSelectedTag(tagObj.tag)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTag === tagObj.tag
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    #{tagObj.tag} ({tagObj.count})
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content - Articles Grid */}
          <div className="lg:col-span-3">
            {articles.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No Articles Found
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery || selectedCategory || selectedTag
                    ? 'Try adjusting your filters or search query'
                    : 'Click "Seed Sample Data" to load sample articles'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {articles.map(article => (
                  <div
                    key={article.id}
                    className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6 cursor-pointer"
                    onClick={() => navigate(`/knowledge/article/${article.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                          {article.category.replace('_', ' ').toUpperCase()}
                        </span>
                        {article.featured && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⭐ Featured
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBookmark(article.id);
                        }}
                        className="text-gray-400 hover:text-teal-600 transition-colors"
                      >
                        {bookmarks.includes(article.id) ? (
                          <BookmarkCheck className="w-5 h-5 text-teal-600 fill-current" />
                        ) : (
                          <Bookmark className="w-5 h-5" />
                        )}
                      </button>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-teal-600">
                      {article.title}
                    </h3>

                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {article.summary}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {article.tags && article.tags.slice(0, 4).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(article.publishedDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="flex items-center">
                          <Eye className="w-4 h-4 mr-1" />
                          {article.views} views
                        </span>
                      </div>
                      <span className="text-gray-600 font-medium">
                        By {article.author}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeHubPage;
